// @ts-nocheck
// Client frame-health telemetry.
//
// The server can't be scraped for what the browser experiences, so we measure it
// here: per-frame render cost, animation-frame gaps, and the gap between server
// snapshots. Once per window we POST a tiny aggregate summary to /clientmetrics;
// the server folds it into Prometheus histograms (see server/metrics.go) that Fly
// scrapes into fly-metrics.net Grafana.
//
// This is the client mirror of the server's tick-health logging, and the whole
// point of the lag investigation: a spike shows up as a high maxDrawMs (our
// render is the culprit), a high maxFrameGap with normal draw (browser/GC/tab
// throttle), or a high maxSnapGap with smooth frames (network / server drop).
//
// Aggregate-only, no PII. Reports only during live play ("playing" state).

const WINDOW_MS = 8000; // report cadence (matches the spirit of server's 10s window)
const LONG_FRAME_MS = 50; // a frame slower than this (~<20fps instant) counts as "long"
const SANE_GAP_MS = 4000; // ignore absurd frame gaps (tab was backgrounded, etc.)

export class FrameHealth {
  constructor() {
    this.lastSnap = 0;
    this.players = 0;
    this.tainted = false;
    // Hidden tabs poison the data: the browser batches WS message processing at
    // ~1Hz, so snapshot() records bogus ~1000ms gaps (the rAF loop itself is
    // paused while hidden, but the gaps land in the still-open window and get
    // flushed after the tab returns). Taint the window on hide and drop it on
    // the next visible tick so background tabs never reach /metrics.
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) this.tainted = true;
      });
    }
    this.maxLongTask = 0;
    // Long-task observer: a main-thread task >50ms blocks the event loop, which
    // delays WS onmessage (→ a snapshot gap / freeze) WITHOUT showing up in our
    // per-frame draw timing. This is the one client stall draw_ms can't see, so
    // we surface it directly.
    if (typeof PerformanceObserver !== "undefined") {
      try {
        this._longTaskObs = new PerformanceObserver((list) => {
          for (const e of list.getEntries()) {
            if (e.duration > this.maxLongTask) this.maxLongTask = e.duration;
          }
        });
        this._longTaskObs.observe({ entryTypes: ["longtask"] });
      } catch (e) { /* unsupported (Safari/Firefox) — just skip */ }
    }
    this.reset(performance.now());
  }

  // resetSnapClock drops the snapshot-gap clock so the next snapshot doesn't
  // register a gap. Called across a reconnect: the long gap while the socket was
  // down is a reconnect artifact, not an in-game freeze, and must not pollute the
  // snapshot-gap metric we use to find real delivery stalls.
  resetSnapClock() {
    this.lastSnap = 0;
  }

  reset(now) {
    this.windowStart = now;
    this.frames = 0;
    this.longFrames = 0;
    this.maxFrameGap = 0;
    this.maxDrawMs = 0;
    this.maxUpdateMs = 0;
    this.maxRenderMs = 0;
    this.maxParticles = 0;
    this.maxSnapGap = 0;
    this.maxLongTask = 0;
  }

  // One animation frame: gapMs = time since previous frame; drawMs = total
  // update+render; updateMs/renderMs split logic from paint so a hitch can be
  // attributed; particleCount is the live scene-complexity proxy.
  frame(gapMs, drawMs, updateMs, renderMs, particleCount) {
    if (gapMs > 0 && gapMs < SANE_GAP_MS) {
      this.frames++;
      if (gapMs > this.maxFrameGap) this.maxFrameGap = gapMs;
      if (gapMs > LONG_FRAME_MS) this.longFrames++;
    }
    if (drawMs > this.maxDrawMs) this.maxDrawMs = drawMs;
    if (updateMs > this.maxUpdateMs) this.maxUpdateMs = updateMs;
    if (renderMs > this.maxRenderMs) this.maxRenderMs = renderMs;
    if (particleCount > this.maxParticles) this.maxParticles = particleCount;
  }

  // A server snapshot arrived (called from onState while playing).
  snapshot(now, players) {
    this.players = players | 0;
    if (this.lastSnap > 0) {
      const gap = now - this.lastSnap;
      if (gap > this.maxSnapGap && gap < SANE_GAP_MS) this.maxSnapGap = gap;
    }
    this.lastSnap = now;
  }

  // Call every frame. Flushes a window when due; `active` gates collection to
  // live play so phase transitions (shop/roundover) don't pollute the data.
  tick(now, rttMs, active) {
    if (this.tainted) {
      // window overlapped a hidden-tab period — discard it unreported
      this.tainted = false;
      this.lastSnap = 0;
      this.reset(now);
      return;
    }
    if (!active) {
      // Between matches: drop the snapshot clock so the next match's first
      // snapshot doesn't register as a giant gap, and don't report idle windows.
      this.lastSnap = 0;
      if (now - this.windowStart >= WINDOW_MS) this.reset(now);
      return;
    }
    if (now - this.windowStart < WINDOW_MS) return;
    if (this.frames > 0) this.send(rttMs);
    this.reset(now);
  }

  send(rttMs) {
    const body = JSON.stringify({
      players: this.players | 0,
      frames: this.frames | 0,
      longFrames: this.longFrames | 0,
      maxFrameGap: Math.round(this.maxFrameGap),
      maxDrawMs: Math.round(this.maxDrawMs * 10) / 10,
      maxUpdateMs: Math.round(this.maxUpdateMs * 10) / 10,
      maxRenderMs: Math.round(this.maxRenderMs * 10) / 10,
      maxParticles: this.maxParticles | 0,
      maxSnapGap: Math.round(this.maxSnapGap),
      maxLongTask: Math.round(this.maxLongTask),
      rttMs: Math.round(rttMs || 0),
    });
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon("/clientmetrics", new Blob([body], { type: "application/json" }));
      } else {
        fetch("/clientmetrics", {
          method: "POST",
          body,
          keepalive: true,
          headers: { "Content-Type": "application/json" },
        }).catch(() => {});
      }
    } catch (e) {
      /* telemetry must never break the game */
    }
  }
}
